/**
 * Écran des règles : profils, historique, et édition du jeu de règles.
 *
 * Chaque profil vit en base et porte un numéro de version. On peut donc régler
 * un barème aujourd'hui, générer un fichier la semaine prochaine, et retrouver
 * exactement ce qui a servi — depuis n'importe quel poste, contrairement à un
 * réglage rangé dans un navigateur.
 */

import { useEffect, useMemo, useRef, useState } from 'react'

import { ApiError } from '@/api/client'
import type { Profile, RuleSet } from '@/api/types'
import { Button, Card, CardBody, CardHead, CardNote, Field, Message, SectionHead } from '@/components/ui'
import { RulesForm } from '@/components/RulesForm'
import {
  useCreateProfile,
  useDeleteProfile,
  useMeta,
  useProfile,
  useProfileVersions,
  useProfiles,
  useResetTemplate,
  useRestoreVersion,
  useSaveProfile,
  useSetDefaultProfile,
  useUploadTemplate,
} from '@/hooks/useApi'
import { downloadJson, formatBytes, formatDateTime } from '@/lib/files'
import { clone, rulesEqual } from '@/lib/rules'
import styles from './RulesPage.module.css'

type Feedback = { kind: 'ok' | 'error' | 'info'; text: string } | null

export function RulesPage() {
  const { data: meta } = useMeta()
  const { data: profileList } = useProfiles()
  const [profileId, setProfileId] = useState('')
  const { data: profile } = useProfile(profileId || undefined)

  const [draft, setDraft] = useState<RuleSet | null>(null)
  const [feedback, setFeedback] = useState<Feedback>(null)
  const [dialog, setDialog] = useState<'create' | 'delete' | null>(null)

  const save = useSaveProfile()
  const setDefault = useSetDefaultProfile()

  useEffect(() => {
    if (!profileId && profileList?.length) {
      setProfileId((profileList.find((item) => item.is_default) ?? profileList[0]!).id)
    }
  }, [profileList, profileId])

  // Le brouillon repart du profil chargé. On le remplace uniquement quand on
  // change de profil ou que sa version avance : sinon un rafraîchissement en
  // arrière-plan effacerait les modifications en cours de saisie.
  const loadedKey = useRef<string>('')
  useEffect(() => {
    if (!profile) return
    const key = `${profile.id}:${profile.version}`
    if (loadedKey.current !== key) {
      loadedKey.current = key
      setDraft(clone(profile.rules))
    }
  }, [profile])

  const dirty = useMemo(
    () => Boolean(profile && draft && !rulesEqual(profile.rules, draft)),
    [profile, draft],
  )

  // Un rechargement de page avec des règles non enregistrées perdrait le
  // travail : le navigateur demande confirmation.
  useEffect(() => {
    if (!dirty) return
    const warn = (event: BeforeUnloadEvent) => event.preventDefault()
    window.addEventListener('beforeunload', warn)
    return () => window.removeEventListener('beforeunload', warn)
  }, [dirty])

  async function persist() {
    if (!profile || !draft) return
    setFeedback(null)
    try {
      await save.mutateAsync({ id: profile.id, rules: draft })
      setFeedback({ kind: 'ok', text: 'Règles enregistrées.' })
    } catch (cause) {
      setFeedback({ kind: 'error', text: cause instanceof ApiError ? cause.message : 'Enregistrement impossible.' })
    }
  }

  if (!meta || !profileList) return null

  return (
    <>
      <div className={styles.bar}>
        <span className={styles.state}>
          <span className={`${styles.dot} ${dirty ? styles.dotDirty : ''}`} />
          {dirty ? (
            <>Modifications non enregistrées</>
          ) : profile ? (
            <>
              <b>Version {profile.version}</b>&nbsp;· enregistrée le {formatDateTime(profile.updated_at)}
            </>
          ) : (
            'Chargement…'
          )}
        </span>

        <div className={styles.actions}>
          <Button
            variant="secondary"
            small
            onClick={() => profile && draft && downloadJson(draft, `regles-${profile.slug}.json`)}
          >
            Exporter (.json)
          </Button>
          <Button
            variant="secondary"
            small
            disabled={!dirty}
            onClick={() => profile && setDraft(clone(profile.rules))}
          >
            Annuler
          </Button>
          <Button small busy={save.isPending} disabled={!dirty} onClick={() => void persist()}>
            Enregistrer
          </Button>
        </div>
      </div>

      {feedback ? <Message kind={feedback.kind}>{feedback.text}</Message> : null}

      <SectionHead title="Règles de génération">
        Chaque jeu de règles est enregistré sur le serveur sous forme de profil versionné : réglez un barème
        aujourd’hui, générez un fichier la semaine prochaine, depuis n’importe quel poste.
      </SectionHead>

      <Card>
        <CardHead title="Profil" />
        <CardBody>
          <div className={styles.profileBar}>
            <Field label="Profil en cours d'édition" htmlFor="profileSelect">
              <select
                id="profileSelect"
                value={profileId}
                onChange={(event) => {
                  if (dirty && !window.confirm('Des modifications ne sont pas enregistrées. Les abandonner ?')) return
                  setProfileId(event.target.value)
                }}
              >
                {profileList.map((item) => (
                  <option key={item.id} value={item.id}>
                    {item.name}
                    {item.is_default ? ' (par défaut)' : ''} — v{item.version}
                  </option>
                ))}
              </select>
            </Field>
            <Button variant="secondary" small onClick={() => setDialog('create')}>
              Nouveau profil
            </Button>
            <Button
              variant="secondary"
              small
              disabled={profile?.is_default || setDefault.isPending}
              onClick={() => profile && void setDefault.mutateAsync(profile.id)}
            >
              Définir par défaut
            </Button>
            <Button variant="danger" small disabled={profileList.length < 2} onClick={() => setDialog('delete')}>
              Supprimer
            </Button>
          </div>
          <CardNote>
            Le profil <b>par défaut</b> est celui proposé au moment de convertir un catalogue. Créez-en d’autres
            pour des barèmes ponctuels — une opération promotionnelle, un fournisseur particulier — sans toucher
            à celui de tous les jours.
          </CardNote>
        </CardBody>
      </Card>

      {draft ? <RulesForm rules={draft} onChange={setDraft} /> : null}

      <AmazonTemplateCard />

      {profile ? <VersionsCard profile={profile} dirty={dirty} /> : null}

      {dialog === 'create' ? (
        <CreateDialog
          currentRules={draft}
          onClose={() => setDialog(null)}
          onCreated={(created) => {
            setProfileId(created.id)
            setDialog(null)
            setFeedback({ kind: 'ok', text: `Profil « ${created.name} » créé.` })
          }}
        />
      ) : null}

      {dialog === 'delete' && profile ? (
        <DeleteDialog
          profile={profile}
          onClose={() => setDialog(null)}
          onDeleted={() => {
            setProfileId('')
            setDialog(null)
            setFeedback({ kind: 'info', text: 'Profil supprimé.' })
          }}
        />
      ) : null}
    </>
  )
}

// --- Modèle Amazon -----------------------------------------------------------

function AmazonTemplateCard() {
  const { data: meta } = useMeta()
  const upload = useUploadTemplate()
  const reset = useResetTemplate()
  const input = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)

  if (!meta) return null
  const template = meta.amazon_template

  async function choose(file: File) {
    setError(null)
    try {
      await upload.mutateAsync(file)
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Téléversement impossible.')
    }
  }

  return (
    <Card>
      <CardHead title="Modèle de fichier Amazon" hint={`${template.attributes} attributs · données ligne ${template.data_row}`} />
      <CardBody>
        <CardNote>
          Les lignes produits sont écrites dans le modèle officiel Amazon, recopié à l’identique. Modèle en
          service : <b>généré le {template.date}</b>, marché {template.language}. Chargez le vôtre s’il est plus
          récent — les colonnes sont retrouvées par nom d’attribut.
        </CardNote>
        <CardNote>
          Le modèle est enregistré sur le serveur : toute l’équipe utilise le même, et il est repris dans la
          sauvegarde de la base.
        </CardNote>

        <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
          <Button variant="secondary" small busy={upload.isPending} onClick={() => input.current?.click()}>
            Utiliser un autre modèle…
          </Button>
          {template.custom ? (
            <>
              <span style={{ fontSize: 13, color: 'var(--text-2)' }}>
                {template.custom_name} · {formatBytes(template.custom_size ?? 0)} · déposé le{' '}
                {formatDateTime(template.custom_uploaded_at)}
              </span>
              <Button variant="secondary" small busy={reset.isPending} onClick={() => void reset.mutateAsync()}>
                Revenir au modèle livré
              </Button>
            </>
          ) : (
            <span style={{ fontSize: 13, color: 'var(--text-3)' }}>Modèle livré avec l’outil</span>
          )}
          <input
            ref={input}
            type="file"
            accept=".xlsm,.xlsx"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0]
              if (file) void choose(file)
              event.target.value = ''
            }}
          />
        </div>

        {error ? <Message kind="error">{error}</Message> : null}
      </CardBody>
    </Card>
  )
}

// --- Historique --------------------------------------------------------------

function VersionsCard({ profile, dirty }: { profile: Profile; dirty: boolean }) {
  const { data: versions } = useProfileVersions(profile.id)
  const restore = useRestoreVersion()
  const [error, setError] = useState<string | null>(null)

  async function rollback(version: number) {
    if (dirty && !window.confirm('Des modifications ne sont pas enregistrées. Les abandonner ?')) return
    setError(null)
    try {
      await restore.mutateAsync({ id: profile.id, version })
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Restauration impossible.')
    }
  }

  return (
    <Card>
      <CardHead title="Historique" hint={`${versions?.length ?? 0} version(s) conservée(s)`} />
      <CardBody>
        <CardNote>
          Revenir à une version passée n’efface rien : cela crée une version neuve avec l’ancien contenu, donc
          la restauration elle-même reste annulable.
        </CardNote>
        <div className={styles.versions}>
          {(versions ?? []).map((version) => (
            <div key={version.version} className={styles.version}>
              <span className={styles.versionNumber}>v{version.version}</span>
              <span className={styles.versionDate}>{formatDateTime(version.created_at)}</span>
              <span className={styles.versionNote}>{version.note ?? ''}</span>
              {version.version === profile.version ? (
                <span className={styles.current}>version en cours</span>
              ) : (
                <Button
                  variant="secondary"
                  small
                  busy={restore.isPending}
                  onClick={() => void rollback(version.version)}
                >
                  Restaurer
                </Button>
              )}
            </div>
          ))}
        </div>
        {error ? <Message kind="error">{error}</Message> : null}
      </CardBody>
    </Card>
  )
}

// --- Boîtes de dialogue -------------------------------------------------------

function Dialog({ title, description, children }: { title: string; description: string; children: React.ReactNode }) {
  return (
    <div className={styles.dialogBackdrop} role="dialog" aria-modal="true" aria-label={title}>
      <div className={styles.dialog}>
        <h3>{title}</h3>
        <p>{description}</p>
        {children}
      </div>
    </div>
  )
}

function CreateDialog({
  currentRules,
  onClose,
  onCreated,
}: {
  currentRules: RuleSet | null
  onClose: () => void
  onCreated: (profile: Profile) => void
}) {
  const create = useCreateProfile()
  const [name, setName] = useState('')
  const [copy, setCopy] = useState(true)
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    try {
      onCreated(await create.mutateAsync({ name, ...(copy && currentRules ? { rules: currentRules } : {}) }))
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Création impossible.')
    }
  }

  return (
    <Dialog
      title="Nouveau profil"
      description="Un profil regroupe un jeu complet de règles : barèmes, valeurs fixes et destinations."
    >
      <Field label="Nom du profil" htmlFor="newProfileName">
        <input
          id="newProfileName"
          type="text"
          autoFocus
          value={name}
          placeholder="Promo été 2026"
          onChange={(event) => setName(event.target.value)}
        />
      </Field>
      <div style={{ marginTop: 14 }}>
        <label className="check" style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 14 }}>
          <input type="checkbox" checked={copy} onChange={(event) => setCopy(event.target.checked)} />
          Partir des règles actuellement affichées
        </label>
      </div>
      {error ? <Message kind="error">{error}</Message> : null}
      <div className={styles.dialogActions}>
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button busy={create.isPending} disabled={!name.trim()} onClick={() => void submit()}>
          Créer
        </Button>
      </div>
    </Dialog>
  )
}

function DeleteDialog({
  profile,
  onClose,
  onDeleted,
}: {
  profile: Profile
  onClose: () => void
  onDeleted: () => void
}) {
  const remove = useDeleteProfile()
  const [error, setError] = useState<string | null>(null)

  async function submit() {
    setError(null)
    try {
      await remove.mutateAsync(profile.id)
      onDeleted()
    } catch (cause) {
      setError(cause instanceof ApiError ? cause.message : 'Suppression impossible.')
    }
  }

  return (
    <Dialog
      title={`Supprimer « ${profile.name} » ?`}
      description="Le profil et tout son historique de versions seront supprimés. Cette action est définitive."
    >
      {error ? <Message kind="error">{error}</Message> : null}
      <div className={styles.dialogActions}>
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button variant="danger" busy={remove.isPending} onClick={() => void submit()}>
          Supprimer
        </Button>
      </div>
    </Dialog>
  )
}
